<template>
<div v-if="data" class="time-series-wrapper">
  <div class="factor-select-area">
    <div class="factor-select-area-header">选择因子</div>
    <div class="divider"></div>
    <div class="factor-select-area-body">
      <ul class="factor-group-select-list">
        <li
          v-for="factorGroup in formatFactorGroups"
          :key="factorGroup.id"
        >
          <div class="factor-group">
            <el-checkbox
              v-model="factorGroupSelectMap[factorGroup.id]"
              :indeterminate="selectedCount[factorGroup.id] > 0 && selectedCount[factorGroup.id] < factorGroup.children.length"
              @change="onFactorGroupCheckChange(factorGroup, ...arguments)"
            >{{factorGroup.name}}</el-checkbox>
          </div>

          <ul class="factor-select-list">
            <li
              v-for="factor in factorGroup.children"
              :key="factor.id"
            >
              <el-checkbox
                v-model="data.factorIds"
                :label="factor.id"
                @change="onFactorSelectChange"
                style="width:100%;"
              >{{factor.name}}</el-checkbox>
            </li>
          </ul>
        </li>
      </ul>
    </div>
    <div class="divider"></div>
    <select-ctrl-group
      :ids="data.factorIds"
      :options="filterFactors"
      @change="onFactorSelectChange"
    />
  </div>

  <div class="time-series-chart">
    <x-y-chart
      v-if="chartData"
      ref="xyChart"
      :index="0"
      :data-config="chartData"
      unit-prop="unit"
      chart-type="line"
      :stack-type="1"
      download-name="PMF时间序列图"
      chart-height="auto"
      need-resize
      selected-change-with-data-changed
      style="height:100%;"
    />
  </div>
</div>
</template>

<script>
import BasePreAnalysis from './BasePreAnalysis'
import SelectCtrlGroup from '../../../../../../components/SelectCtrlGroup/index'
import XYChart from '../../../../../../components/DataChart/XYChart'

export default {
  name: 'TimeSeries',
  components: {XYChart, SelectCtrlGroup},
  label: '时间序列图',
  dataKey: 'TimeSeries',
  extends: BasePreAnalysis,
  data: () => ({
    factorGroupSelectMap: {},
    chartData: null
  }),
  computed: {
    filterFactors () {
      const {factorMap} = this
      return this.condition.factorIds.map(factorId => ({
        id: factorId,
        name: factorMap[factorId].name
      }))
    },
    selectedCount () {
      const {factorMap} = this

      return this.data.factorIds.reduce((map, factorId) => {
        const factor = factorMap[factorId]

        if (factor) {
          const {parentId} = factor

          if (map.hasOwnProperty(parentId)) {
            map[parentId] += 1
          }
        }

        return map
      }, this.formatFactorGroups.reduce((map, factorGroup) => {
        map[factorGroup.id] = 0
        return map
      }, {}))
    }
  },
  methods: {
    getDefaultData () {
      return Promise.resolve({
        factorIds: [this.condition.factorIds[0]]
      })
    },
    buildFactorGroupSelectMap () {
      const {selectedCount} = this
      this.factorGroupSelectMap = this.formatFactorGroups.reduce((map, factorGroup) => {
        const groupId = factorGroup.id
        map[groupId] = factorGroup.children.length === selectedCount[groupId]
        return map
      }, {})
    },
    ready () {
      this.buildFactorGroupSelectMap()
      this.renderChart()
    },
    renderChart () {
      const {data: {factorIds}, shareData, factorMap, dataTSs, site} = this
      this.chartData = Object.freeze({
        id: site.id,
        name: '时间序列',
        dataTimes: dataTSs.map(ts => [ts, new Date(ts).format('YYYY/MM/DD HH:mm:ss')]),
        dimensions: factorIds.sort((a, b) => a.localeCompare(b)).map(factorId => ({
          id: factorId,
          factorId,
          name: factorMap[factorId].name,
          unit: 'μg/m3',
          seriesParam: {
            type: 'line',
            showSymbol: false
          }
        })),
        seriesMap: factorIds.reduce((map, factorId) => {
          const data = shareData[factorId]

          map[factorId] = data ? Object.values(data).reduce((map, data) => {
            const {dataTime} = data
            const formatDataTime = dataTime.format('YYYY/MM/DD HH:mm:ss')
            map[formatDataTime] = {
              dataTime: formatDataTime,
              ts: dataTime.getTime(),
              value: data.conc,
              flag: null
            }

            return map
          }, {}) : {}

          return map
        }, {})
      })
    },
    onFactorGroupCheckChange (factorGroup, checked) {
      const {data} = this
      const {factorIds} = data

      if (checked) {
        const factorSelectedMap = factorIds.reduce((map, factorId) => {
          map[factorId] = true
          return map
        }, {})

        factorGroup.children.forEach(factor => {
          const factorId = factor.id

          if (!factorSelectedMap[factorId]) {
            factorIds.push(factorId)
          }
        })
      } else {
        const factorGroupFactorMap = factorGroup.children.reduce((map, factor) => {
          map[factor.id] = true
          return map
        }, {})
        data.factorIds = factorIds.filter(factorId => !factorGroupFactorMap[factorId])
      }
    },
    onFactorSelectChange () {
      this.ready()
    }
  }
}
</script>

<style lang="scss" scoped>
.time-series-wrapper {
  display: grid;
  grid-template-columns: minmax(max-content, 150px) auto;
  grid-column-gap: 12px;
  padding-top: 12px;

  > * {
    min: {
      width: 0;
      height: 0;
    }
  }
}

.factor-select-area {
  display: grid;
  grid-template-rows: max-content 1px auto 1px max-content;
  border: 1px solid #EBEEF5;

  > * {
    min: {
      width: 0;
      height: 0;
    }
  }

  > .factor-select-area-header {
    font: {
      weight: 700;
    };
    height: 35px;
    line-height: 35px;
    padding-left: 10px;
    color: #303133;
    background-color: #f9f9f9;
  }

  > .divider {
    background-color: #EBEEF5;
  }

  > .factor-select-area-body {
    padding: 3px 10px;
    overflow: auto;
  }

  .factor-group-select-list {
    margin: 0;
    padding: 0;
    line-height: 32px;
    list-style: none;
  }

  .factor-select-list {
    font: {
      size: 14px;
    };
    margin: 0;
    padding: 0;
    line-height: 32px;
    list-style: none;

    > li {
      cursor: pointer;

      &:hover {
        background-color: #F5F7FA;
      }

      &.selected {
        background-color: #ecf5ff;
      }

      > .el-checkbox {
        padding: 0 10px;
      }
    }
  }
}

.time-series-chart {
  padding-top: 20px;
  border: 1px solid #EBEEF5;
}
</style>



// WEBPACK FOOTER //
// src/views/04Statistical-Analysis/11PMF-Analysis/01PM/01Run/preAnalysisComponents/03TimeSeries.vue